import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Users, BookOpen, Star, Plus, Trash2, Save, RefreshCw, Volume2, Image as ImageIcon, Upload, LayoutGrid, List as ListIcon, Pencil, Check, AlertTriangle, Download, Database, ShieldAlert, Loader2, Inbox, X as XIcon, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, playWord, clearWordAudioCache, WORD_VOICE_PRIMARY, WORD_VOICE_SECONDARY } from "../lib/api";
import WordImage from "../components/WordImage";
import WordConfirmModal from "../components/WordConfirmModal";

const TABS = [
  { id: "stats", label: "Overview", icon: Star },
  { id: "schools", label: "Schools", icon: Building2 },
  { id: "users", label: "Users", icon: Users },
  { id: "packs", label: "Word Packs", icon: BookOpen },
  { id: "image_requests", label: "Image Requests", icon: Inbox },
  { id: "defaults", label: "Defaults", icon: Star },
  { id: "tools", label: "Tools", icon: Wrench },
  { id: "backup", label: "Backup", icon: Database },
];

export default function Admin() {
  const [tab, setTab] = useState("stats");
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-bold text-[#FF6B6B] uppercase tracking-widest">Admin</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">BuzzSpell Admin</h1>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-b-2 border-blue-100 pb-3" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm border-2 transition-colors ${
                active ? "bg-[#4D96FF] text-white border-[#1D4ED8]" : "bg-white text-[#1E1B4B] border-blue-200 hover:bg-blue-50"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={3} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "stats" && <StatsPanel />}
      {tab === "schools" && <SchoolsPanel />}
      {tab === "users" && <UsersPanel />}
      {tab === "packs" && <PacksPanel />}
      {tab === "image_requests" && <ImageRequestsPanel />}
      {tab === "defaults" && <DefaultsPanel />}
      {tab === "tools" && <ToolsPanel />}
      {tab === "backup" && <BackupPanel />}
    </div>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch { toast.error("Could not load stats"); }
  })(); }, []);
  if (!stats) return <div className="card-soft text-center py-8 text-slate-500">Loading…</div>;
  const items = [
    { label: "Users", value: stats.users, color: "bg-[#4D96FF]", shadow: "#1D4ED8" },
    { label: "Schools", value: stats.schools, color: "bg-[#10B981]", shadow: "#047857" },
    { label: "Word Packs", value: stats.packs, color: "bg-[#FBBF24]", shadow: "#B45309" },
    { label: "Global Packs", value: stats.global_packs, color: "bg-[#FF6B6B]", shadow: "#9F1239" },
    { label: "Total Attempts", value: stats.attempts, color: "bg-[#1E1B4B]", shadow: "#0F0E2C" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats-grid">
      {items.map((it) => (
        <motion.div
          key={it.label}
          initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="card-soft text-center"
        >
          <div className={`mx-auto w-12 h-12 rounded-2xl ${it.color} flex items-center justify-center mb-2`} style={{ boxShadow: `0 5px 0 ${it.shadow}` }}>
            <Star className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="text-3xl font-display font-bold text-[#1E1B4B]">{it.value}</div>
          <div className="text-xs font-bold text-slate-500 uppercase">{it.label}</div>
        </motion.div>
      ))}
    </div>
  );
}

function SchoolsPanel() {
  const [schools, setSchools] = useState([]);
  const [name, setName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/schools");
      // Backend sorts alphabetically; re-sort defensively for safety.
      const sorted = [...(data || [])].sort((a, b) =>
        (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
      );
      setSchools(sorted);
    } catch { toast.error("Could not load schools"); }
  };
  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/schools", { name: name.trim() });
      setName("");
      load();
      toast.success("School added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add school");
    }
  };

  const onApprove = async (sid) => {
    try {
      await api.post(`/admin/schools/${sid}/approve`);
      load();
      toast.success("School approved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    }
  };

  const onDelete = async (sid) => {
    if (!window.confirm("Delete this school? Users assigned to it will be unassigned.")) return;
    try { await api.delete(`/admin/schools/${sid}`); load(); toast.success("School deleted"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  const pendingCount = schools.filter((s) => s.status === "pending").length;

  return (
    <div className="space-y-4" data-testid="admin-schools-panel">
      <form onSubmit={onAdd} className="card-soft flex gap-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="New school name"
          className="flex-1 rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-4 py-3 outline-none font-semibold"
          data-testid="admin-school-name-input"
        />
        <button type="submit" className="btn-clay-blue" data-testid="admin-school-add-btn">
          <Plus className="w-5 h-5" strokeWidth={3} /> Add
        </button>
      </form>
      {pendingCount > 0 && (
        <div
          className="card-soft bg-amber-50 border-amber-200 text-amber-900 font-semibold text-sm"
          data-testid="admin-schools-pending-banner"
        >
          {pendingCount} school{pendingCount === 1 ? "" : "s"} waiting for approval. Approve below to make them visible to new users.
        </div>
      )}
      <div className="card-soft">
        {schools.length === 0 ? (
          <div className="text-center py-8 text-slate-500 font-semibold">No schools yet</div>
        ) : (
          <ul className="divide-y divide-blue-100">
            {schools.map((s) => {
              const isPending = s.status === "pending";
              return (
                <li key={s.id} className="flex items-center justify-between py-3" data-testid={`admin-school-row-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-[#1E1B4B] flex items-center gap-2">
                      <span className="truncate">{s.name}</span>
                      {isPending && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide border border-amber-200"
                          data-testid={`admin-school-pending-${s.id}`}
                        >
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isPending && (
                      <button
                        onClick={() => onApprove(s.id)}
                        className="text-[#22C55E] hover:bg-emerald-50 rounded-full p-2"
                        data-testid={`admin-school-approve-${s.id}`}
                        title="Approve"
                      >
                        <Check className="w-5 h-5" strokeWidth={3} />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(s.id)}
                      className="text-[#F43F5E] hover:bg-rose-50 rounded-full p-2"
                      data-testid={`admin-school-delete-${s.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" strokeWidth={3} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [u, s, g] = await Promise.all([
        api.get("/admin/users"),
        api.get("/schools"),
        api.get("/grades"),
      ]);
      setUsers(u.data);
      setSchools(s.data);
      setGrades(g.data);
    } catch { toast.error("Could not load users"); }
  };
  useEffect(() => { load(); }, []);

  const schoolName = (id) => schools.find((s) => s.id === id)?.name || "—";

  const onSaveEdit = async () => {
    try {
      await api.patch(`/admin/users/${editing.id}`, {
        name: editing.name,
        role: editing.role,
        age: editing.age ? parseInt(editing.age, 10) : null,
        school_id: editing.school_id || "",
        grade_level: editing.grade_level || "",
      });
      setEditing(null);
      load();
      toast.success("User updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save user");
    }
  };

  const onDelete = async (uid) => {
    if (!window.confirm("Delete this user and all their attempts?")) return;
    try { await api.delete(`/admin/users/${uid}`); load(); toast.success("User deleted"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-bold uppercase text-slate-500 border-b-2 border-blue-100">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">School</th>
              <th className="py-2 pr-3">Grade</th>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2 pr-3">Last active</th>
              <th className="py-2 pr-3">Attempts</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-blue-50 hover:bg-blue-50/50" data-testid={`admin-user-row-${u.id}`}>
                <td className="py-2 pr-3 font-bold text-[#1E1B4B]">{u.name}</td>
                <td className="py-2 pr-3">{u.email}</td>
                <td className="py-2 pr-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    u.role === "admin" ? "bg-rose-100 text-[#9F1239]" : u.role === "teacher" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"
                  }`}>
                    {u.role || "student"}
                  </span>
                </td>
                <td className="py-2 pr-3">{schoolName(u.school_id)}</td>
                <td className="py-2 pr-3">{u.grade_level || "—"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{u.last_active_at ? new Date(u.last_active_at).toLocaleDateString() : "—"}</td>
                <td className="py-2 pr-3 text-xs">{u.attempts || 0}</td>
                <td className="py-2 pr-3 flex gap-1">
                  <button onClick={() => setEditing({ ...u })} className="text-[#4D96FF] hover:bg-blue-50 rounded-full p-1.5" data-testid={`admin-user-edit-${u.id}`} title="Edit">
                    <Save className="w-4 h-4" strokeWidth={3} />
                  </button>
                  <button onClick={() => onDelete(u.id)} className="text-[#F43F5E] hover:bg-rose-50 rounded-full p-1.5" data-testid={`admin-user-delete-${u.id}`} title="Delete">
                    <Trash2 className="w-4 h-4" strokeWidth={3} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="card-soft space-y-3" data-testid="admin-user-edit-panel">
          <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Editing {editing.email}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-4 py-3 outline-none font-semibold" placeholder="Name" data-testid="admin-edit-name" />
            <select value={editing.role || "student"} onChange={(e) => setEditing({ ...editing, role: e.target.value })}
              className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-3 outline-none font-semibold bg-white" data-testid="admin-edit-role">
              <option value="student">student</option>
              <option value="teacher">teacher</option>
              <option value="admin">admin</option>
            </select>
            <input type="number" value={editing.age || ""} onChange={(e) => setEditing({ ...editing, age: e.target.value })}
              className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-4 py-3 outline-none font-semibold" placeholder="Age" data-testid="admin-edit-age" />
            <select value={editing.school_id || ""} onChange={(e) => setEditing({ ...editing, school_id: e.target.value })}
              className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-3 outline-none font-semibold bg-white" data-testid="admin-edit-school">
              <option value="">— no school —</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={editing.grade_level || ""} onChange={(e) => setEditing({ ...editing, grade_level: e.target.value })}
              className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-3 outline-none font-semibold bg-white" data-testid="admin-edit-grade">
              <option value="">— no grade —</option>
              {grades.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={onSaveEdit} className="btn-clay-blue" data-testid="admin-edit-save-btn">
              <Save className="w-5 h-5" strokeWidth={3} /> Save
            </button>
            <button onClick={() => setEditing(null)} className="btn-clay-ghost" data-testid="admin-edit-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PacksPanel() {
  const [packs, setPacks] = useState([]);
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newWord, setNewWord] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadGrade, setUploadGrade] = useState("");
  const [uploadSchool, setUploadSchool] = useState("");
  const [uploadVoice, setUploadVoice] = useState("openai");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null); // { words, suggestedName, source } | null
  const [busy, setBusy] = useState({});
  const [view, setView] = useState("list"); // "list" | "card"
  // word being inline-edited: { original, draft } or null
  const [editingWord, setEditingWord] = useState(null);
  // per-(word, voice) audio health: "ok" | "silent" | "missing"
  const [health, setHealth] = useState({});

  const load = async () => {
    try {
      const [p, s, g] = await Promise.all([
        api.get("/admin/wordpacks"),
        api.get("/schools"),
        api.get("/grades"),
      ]);
      setPacks(p.data);
      setSchools(s.data);
      setGrades(g.data);
    } catch { toast.error("Could not load packs"); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Pick a PDF or image first"); return; }
    const lower = uploadFile.name.toLowerCase();
    const ok = lower.endsWith(".pdf") || /\.(png|jpe?g|webp|heic|heif)$/.test(lower);
    if (!ok) { toast.error("Please pick a PDF or an image"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    setExtracting(true);
    try {
      const { data } = await api.post("/extract-words", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReview({
        words: data.words || [],
        suggestedName: uploadName || data.suggested_name || uploadFile.name.replace(/\.[^.]+$/, ""),
        source: data.source || (lower.endsWith(".pdf") ? "pdf" : "image"),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the file");
    } finally { setExtracting(false); }
  };

  const handleConfirm = async ({ name, words }) => {
    setSaving(true);
    try {
      const payload = {
        name,
        words,
        voice_provider: uploadVoice,
      };
      if (uploadGrade) payload.grade_level = uploadGrade;
      if (uploadSchool) {
        payload.school_id = uploadSchool;
        payload.is_global = false;
      } else {
        payload.is_global = true;
      }
      await api.post("/admin/wordpacks", payload);
      setUploadFile(null);
      setUploadName("");
      setUploadGrade("");
      setUploadSchool("");
      setUploadVoice("openai");
      setReview(null);
      load();
      toast.success("Pack created — audio & images will generate in the background");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create pack");
    } finally { setSaving(false); }
  };

  const onPackUpdate = async (pid, patch) => {
    try { await api.patch(`/admin/wordpacks/${pid}`, patch); load(); toast.success("Pack updated"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not update pack"); }
  };

  const onDeletePack = async (pid) => {
    if (!window.confirm("Delete this word pack?")) return;
    try { await api.delete(`/admin/wordpacks/${pid}`); setSelected(null); load(); toast.success("Pack deleted"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete pack"); }
  };

  const onAddWord = async () => {
    const w = newWord.trim().toLowerCase();
    if (!w) return;
    try {
      await api.post(`/admin/wordpacks/${selected.id}/words`, { word: w });
      setNewWord("");
      const { data } = await api.get("/admin/wordpacks");
      setPacks(data);
      const fresh = data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
      toast.success(`Added "${w}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add word");
    }
  };

  const onRemoveWord = async (w) => {
    try {
      await api.delete(`/admin/wordpacks/${selected.id}/words/${encodeURIComponent(w)}`);
      const { data } = await api.get("/admin/wordpacks");
      setPacks(data);
      const fresh = data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove word");
    }
  };

  const onStartEditWord = (w) => setEditingWord({ original: w, draft: w });

  const onCancelEditWord = () => setEditingWord(null);

  const onSaveEditWord = async () => {
    if (!editingWord) return;
    const draft = (editingWord.draft || "").trim().toLowerCase();
    if (!draft || !/^[a-z]+$/.test(draft)) {
      toast.error("Word must be letters only");
      return;
    }
    if (draft === editingWord.original) {
      setEditingWord(null);
      return;
    }
    try {
      await api.patch(
        `/admin/wordpacks/${selected.id}/words/${encodeURIComponent(editingWord.original)}`,
        { word: draft },
      );
      // Editing intentionally does NOT regenerate audio/image — admin can
      // hit the per-voice / image regen buttons next to the row when ready.
      const { data } = await api.get("/admin/wordpacks");
      setPacks(data);
      const fresh = data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
      setEditingWord(null);
      // Refresh health view: the new word will likely be 'missing' until
      // assets are regenerated, the old word's row disappears.
      loadHealth(selected.id);
      toast.success(`Renamed to "${draft}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rename word");
    }
  };

  // Audio-health is a lightweight per-(word,voice) status derived from the
  // peak amplitude stored at generation time. We fetch it once per pack
  // selection and again after any regen so the indicator stays accurate
  // without re-decoding bytes on every render.
  const loadHealth = async (packId) => {
    try {
      const { data } = await api.get(`/admin/wordpacks/${packId}/audio-health`);
      setHealth(data.health || {});
    } catch (err) {
      console.warn("[admin] health load failed:", err);
    }
  };

  useEffect(() => {
    if (!selected?.id) { setHealth({}); return; }
    loadHealth(selected.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const onRegenAudio = async (w, voice) => {
    // `voice` is optional. When omitted, regenerate every voice we use.
    const busyKey = voice ? `a-${voice}-${w}` : `a-${w}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      const url = voice
        ? `/admin/words/${encodeURIComponent(w)}/regenerate-audio?voice=${encodeURIComponent(voice)}`
        : `/admin/words/${encodeURIComponent(w)}/regenerate-audio`;
      await api.post(url);
      // Evict the stale decoded buffer from the in-memory cache so the next
      // "Play Female/Male" press fetches the freshly-regenerated bytes
      // instead of replaying the browser-cached version. Without this, the
      // admin would have to refresh the page to hear the regen result.
      clearWordAudioCache(w, voice);
      toast.success(voice ? `${voice} audio regenerated: ${w}` : `Audio regenerated: ${w}`);
      // Refresh health for this pack so the indicator reflects the new take.
      if (selected?.id) loadHealth(selected.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate audio");
    } finally { setBusy((b) => ({ ...b, [busyKey]: false })); }
  };

  const onPlayVoice = async (w, voice) => {
    try { await playWord(w, voice); }
    catch { toast.error("Couldn't play audio"); }
  };

  const onRegenAudioElevenLabs = async (w, voice) => {
    // ElevenLabs fallback path. Same per-(word,voice) cache replacement as
    // OpenAI, but routed to the ElevenLabs endpoint so admins can recover
    // words OpenAI can't say cleanly. UI behaves identically — busy spinner,
    // local cache eviction, health refresh.
    const busyKey = `el-${voice}-${w}`;
    setBusy((b) => ({ ...b, [busyKey]: true }));
    try {
      await api.post(
        `/admin/words/${encodeURIComponent(w)}/regenerate-audio-elevenlabs?voice=${encodeURIComponent(voice)}`,
      );
      clearWordAudioCache(w, voice);
      toast.success(`ElevenLabs ${voice} audio regenerated: ${w}`);
      if (selected?.id) loadHealth(selected.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ElevenLabs regen failed");
    } finally { setBusy((b) => ({ ...b, [busyKey]: false })); }
  };

  const onRegenImage = async (w) => {
    setBusy((b) => ({ ...b, [`i-${w}`]: true }));
    try { await api.post(`/admin/words/${encodeURIComponent(w)}/regenerate-image`); toast.success(`Image regenerated: ${w}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not regenerate image"); }
    finally { setBusy((b) => ({ ...b, [`i-${w}`]: false })); }
  };

  return (
    <div className="space-y-4" data-testid="admin-packs-panel">
      <form onSubmit={onUpload} className="card-soft space-y-2" data-testid="admin-pack-upload-form">
        <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Upload new word pack (PDF or photo)</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="file"
            accept=".pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            className="font-semibold"
            data-testid="admin-pack-file-input"
          />
          <input value={uploadName} onChange={(e) => setUploadName(e.target.value)}
            placeholder="Pack name (optional)"
            className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold flex-1 min-w-[180px]"
            data-testid="admin-pack-name-input" />
          <select value={uploadGrade} onChange={(e) => setUploadGrade(e.target.value)}
            className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white"
            data-testid="admin-pack-grade-input">
            <option value="">— grade (optional) —</option>
            {grades.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={uploadSchool} onChange={(e) => setUploadSchool(e.target.value)}
            className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white"
            data-testid="admin-pack-school-input">
            <option value="">— global (no school) —</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={uploadVoice} onChange={(e) => setUploadVoice(e.target.value)}
            className="rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white"
            data-testid="admin-pack-voice-input"
            title="TTS provider used to pre-generate audio">
            <option value="openai">Voice: OpenAI</option>
            <option value="elevenlabs">Voice: ElevenLabs</option>
          </select>
          <button type="submit" className="btn-clay-blue" disabled={extracting} data-testid="admin-pack-upload-btn">
            <Upload className="w-5 h-5" strokeWidth={3} /> {extracting ? "Reading…" : "Review words"}
          </button>
        </div>
        <p className="text-xs text-slate-500 font-semibold">
          We'll show you the detected words and let you edit them before any audio or images are generated.
        </p>
      </form>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        <div className="card-soft" data-testid="admin-packs-list">
          <h3 className="font-display text-lg font-bold text-[#1E1B4B] mb-3">Word packs ({packs.length})</h3>
          <ul className="space-y-1">
            {packs.map((p) => (
              <li key={p.id}>
                <button onClick={() => setSelected(p)}
                  className={`w-full text-left px-3 py-2 rounded-xl font-semibold transition-colors ${
                    selected?.id === p.id ? "bg-[#4D96FF] text-white" : "hover:bg-blue-50 text-[#1E1B4B]"
                  }`}
                  data-testid={`admin-pack-row-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{p.name}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {p.silent_count > 0 && (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                            selected?.id === p.id ? "bg-white text-[#F59E0B]" : "bg-amber-100 text-amber-800"
                          }`}
                          title={`${p.silent_count} word(s) with near-silent audio`}
                          data-testid={`admin-pack-silent-badge-${p.id}`}
                        >
                          {p.silent_count} <AlertTriangle className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                      )}
                      <span className="text-xs opacity-80">{p.word_count}</span>
                    </span>
                  </div>
                  <div className="text-xs opacity-75">
                    {p.is_global ? "Global" : (p.owner ? `${p.owner.email}` : "User")} · {(p.available_grades || []).join(", ") || "no grade"}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {selected ? (
          <div className="card-soft" data-testid="admin-pack-detail">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-[200px]">
                <input value={selected.name}
                  onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                  onBlur={() => onPackUpdate(selected.id, { name: selected.name })}
                  className="font-display text-2xl font-bold text-[#1E1B4B] w-full bg-transparent border-b-2 border-blue-100 focus:border-[#4D96FF] outline-none"
                  data-testid="admin-pack-name-edit" />
                <div className="text-xs text-slate-500 mt-1">{selected.word_count} words · {selected.is_global ? "Global" : "User-owned"}</div>
              </div>
              <button onClick={() => onDeletePack(selected.id)} className="text-[#F43F5E] hover:bg-rose-50 rounded-full p-2" data-testid="admin-pack-delete-btn">
                <Trash2 className="w-5 h-5" strokeWidth={3} />
              </button>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs font-bold uppercase text-slate-500">Grade(s)</label>
                <select multiple value={selected.available_grades || []}
                  onChange={(e) => {
                    const sel = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setSelected({ ...selected, available_grades: sel });
                  }}
                  onBlur={() => onPackUpdate(selected.id, { available_grades: selected.available_grades || [] })}
                  className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white h-32"
                  data-testid="admin-pack-grades-edit">
                  {grades.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <div className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple</div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500">School</label>
                <select value={selected.school_id || ""}
                  onChange={(e) => {
                    const sid = e.target.value;
                    setSelected({ ...selected, school_id: sid || null });
                    onPackUpdate(selected.id, { school_id: sid });
                  }}
                  className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white"
                  data-testid="admin-pack-school-edit">
                  <option value="">— no school —</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="text-xs text-slate-500 mt-1">Limits the pack to a specific school's users</div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-slate-500">Visibility</label>
                <select value={selected.is_global ? "global" : "private"}
                  onChange={(e) => {
                    const isG = e.target.value === "global";
                    setSelected({ ...selected, is_global: isG });
                    onPackUpdate(selected.id, { is_global: isG });
                  }}
                  className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-3 py-2 outline-none font-semibold bg-white"
                  data-testid="admin-pack-visibility-edit">
                  <option value="global">Global (all users)</option>
                  <option value="private">User-owned only</option>
                </select>
              </div>
            </div>

            <div className="mb-3 flex gap-2">
              <input value={newWord} onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddWord(); } }}
                placeholder="Add a word…"
                className="flex-1 rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-4 py-2 outline-none font-semibold"
                data-testid="admin-pack-newword-input" />
              <button onClick={onAddWord} className="btn-clay-blue" data-testid="admin-pack-newword-btn">
                <Plus className="w-5 h-5" strokeWidth={3} /> Add word
              </button>
            </div>

            <div className="flex items-center justify-between gap-2 mb-2" data-testid="admin-pack-view-toggle">
              <div className="text-xs font-bold uppercase text-slate-500">Words ({(selected.words || []).length})</div>
              <div className="inline-flex rounded-full border-2 border-blue-200 bg-white overflow-hidden">
                <button
                  onClick={() => setView("list")}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold transition-colors ${view === "list" ? "bg-[#4D96FF] text-white" : "text-[#1E1B4B] hover:bg-blue-50"}`}
                  data-testid="admin-pack-view-list-btn"
                >
                  <ListIcon className="w-3.5 h-3.5" strokeWidth={3} /> List View
                </button>
                <button
                  onClick={() => setView("card")}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold transition-colors ${view === "card" ? "bg-[#4D96FF] text-white" : "text-[#1E1B4B] hover:bg-blue-50"}`}
                  data-testid="admin-pack-view-card-btn"
                >
                  <LayoutGrid className="w-3.5 h-3.5" strokeWidth={3} /> Card View
                </button>
              </div>
            </div>

            {view === "list" ? (
              <div className="overflow-x-auto" data-testid="admin-pack-word-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-bold uppercase text-slate-500 border-b-2 border-blue-100">
                      <th className="py-2 pr-2 w-10">#</th>
                      <th className="py-2 pr-3">Word</th>
                      <th className="py-2 pr-3">Audio</th>
                      <th className="py-2 pr-3">Image</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.words || []).map((w, idx) => {
                      const isEditing = editingWord?.original === w;
                      const h = health[w] || {};
                      return (
                        <tr key={w} className="border-b border-blue-50" data-testid={`admin-word-row-${w}`}>
                          <td className="py-2 pr-2 text-xs font-bold text-slate-400 tabular-nums" data-testid={`admin-word-number-${w}`}>{idx + 1}</td>
                          <td className="py-2 pr-3 font-bold text-[#1E1B4B]">
                            {isEditing ? (
                              <div className="flex gap-1 items-center">
                                <input
                                  autoFocus
                                  value={editingWord.draft}
                                  onChange={(e) => setEditingWord({ ...editingWord, draft: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); onSaveEditWord(); }
                                    if (e.key === "Escape") onCancelEditWord();
                                  }}
                                  className="rounded-lg border-2 border-[#4D96FF] focus:border-[#1D4ED8] px-2 py-1 outline-none font-bold text-[#1E1B4B] w-32"
                                  data-testid={`admin-word-edit-input-${w}`}
                                />
                                <button onClick={onSaveEditWord} className="text-[#10B981] hover:bg-emerald-50 rounded-full p-1" title="Save" data-testid={`admin-word-edit-save-${w}`}>
                                  <Check className="w-4 h-4" strokeWidth={3} />
                                </button>
                                <button onClick={onCancelEditWord} className="text-slate-400 hover:bg-slate-50 rounded-full p-1" title="Cancel" data-testid={`admin-word-edit-cancel-${w}`}>
                                  <Trash2 className="w-4 h-4" strokeWidth={3} />
                                </button>
                              </div>
                            ) : w}
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <button onClick={() => onPlayVoice(w, WORD_VOICE_PRIMARY)} className="text-[#EC4899] hover:bg-pink-50 rounded-full p-1.5" title="Play female voice" data-testid={`admin-word-play-female-${w}`}>
                                <Volume2 className="w-4 h-4" strokeWidth={3} />
                              </button>
                              <AudioHealthDot status={h[WORD_VOICE_PRIMARY]} />
                              <button onClick={() => onPlayVoice(w, WORD_VOICE_SECONDARY)} className="text-[#1D4ED8] hover:bg-blue-50 rounded-full p-1.5 ml-1" title="Play male voice" data-testid={`admin-word-play-male-${w}`}>
                                <Volume2 className="w-4 h-4" strokeWidth={3} />
                              </button>
                              <AudioHealthDot status={h[WORD_VOICE_SECONDARY]} />
                              <span className="mx-1 h-4 w-px bg-blue-100" />
                              <button onClick={() => onRegenAudio(w, WORD_VOICE_PRIMARY)} disabled={busy[`a-${WORD_VOICE_PRIMARY}-${w}`]} className="text-[#EC4899] hover:bg-pink-50 rounded-full p-1.5 disabled:opacity-40" title="Regenerate female audio (OpenAI)" data-testid={`admin-word-regen-female-${w}`}>
                                <RefreshCw className={`w-4 h-4 ${busy[`a-${WORD_VOICE_PRIMARY}-${w}`] ? "animate-spin" : ""}`} strokeWidth={3} />
                              </button>
                              <button onClick={() => onRegenAudio(w, WORD_VOICE_SECONDARY)} disabled={busy[`a-${WORD_VOICE_SECONDARY}-${w}`]} className="text-[#1D4ED8] hover:bg-blue-50 rounded-full p-1.5 disabled:opacity-40" title="Regenerate male audio (OpenAI)" data-testid={`admin-word-regen-male-${w}`}>
                                <RefreshCw className={`w-4 h-4 ${busy[`a-${WORD_VOICE_SECONDARY}-${w}`] ? "animate-spin" : ""}`} strokeWidth={3} />
                              </button>
                              <span className="mx-1 h-4 w-px bg-blue-100" />
                              <button onClick={() => onRegenAudioElevenLabs(w, WORD_VOICE_PRIMARY)} disabled={busy[`el-${WORD_VOICE_PRIMARY}-${w}`]} className="px-2 h-6 rounded-full bg-pink-100 text-[#9F1239] text-[10px] font-bold tracking-wide hover:bg-pink-200 disabled:opacity-40 inline-flex items-center gap-1" title="Regenerate female audio with ElevenLabs" data-testid={`admin-word-el-female-${w}`}>
                                {busy[`el-${WORD_VOICE_PRIMARY}-${w}`] ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={3} /> : null} EL
                              </button>
                              <button onClick={() => onRegenAudioElevenLabs(w, WORD_VOICE_SECONDARY)} disabled={busy[`el-${WORD_VOICE_SECONDARY}-${w}`]} className="px-2 h-6 rounded-full bg-blue-100 text-[#1D4ED8] text-[10px] font-bold tracking-wide hover:bg-blue-200 disabled:opacity-40 inline-flex items-center gap-1" title="Regenerate male audio with ElevenLabs" data-testid={`admin-word-el-male-${w}`}>
                                {busy[`el-${WORD_VOICE_SECONDARY}-${w}`] ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={3} /> : null} EL
                              </button>
                            </div>
                          </td>
                          <td className="py-2 pr-3">
                            <button onClick={() => onRegenImage(w)} disabled={busy[`i-${w}`]} className="text-amber-700 hover:bg-amber-50 rounded-full p-1.5 disabled:opacity-40" title="Regenerate image" data-testid={`admin-word-regen-image-${w}`}>
                              <RefreshCw className={`w-4 h-4 ${busy[`i-${w}`] ? "animate-spin" : ""}`} strokeWidth={3} />
                            </button>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => onStartEditWord(w)} disabled={isEditing} className="text-[#4D96FF] hover:bg-blue-50 rounded-full p-1 disabled:opacity-40" title="Edit word text" data-testid={`admin-word-edit-${w}`}>
                                <Pencil className="w-4 h-4" strokeWidth={3} />
                              </button>
                              <button onClick={() => onRemoveWord(w)} className="text-[#F43F5E] hover:bg-rose-50 rounded-full p-1" data-testid={`admin-word-remove-${w}`} title="Remove">
                                <Trash2 className="w-4 h-4" strokeWidth={3} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
                data-testid="admin-pack-word-cards"
              >
                {(selected.words || []).map((w, idx) => (
                  <WordCard
                    key={w}
                    word={w}
                    index={idx + 1}
                    busy={busy}
                    health={health[w] || {}}
                    onPlayVoice={onPlayVoice}
                    onRegenAudio={onRegenAudio}
                    onRegenAudioElevenLabs={onRegenAudioElevenLabs}
                    onRegenImage={onRegenImage}
                    onRemoveWord={onRemoveWord}
                  />
                ))}
              </div>
            )}
            <BulkActionsBar pack={selected} onComplete={() => { load(); if (selected?.id) loadHealth(selected.id); }} />
            <PackPreviewLegend />
          </div>
        ) : (
          <div className="card-soft text-center py-12 text-slate-500 font-semibold">Pick a pack on the left to manage its words</div>
        )}
      </div>

      <WordConfirmModal
        open={!!review}
        detectedWords={review?.words || []}
        suggestedName={review?.suggestedName || ""}
        source={review?.source || "pdf"}
        submitting={saving}
        nameLabel="Pack name"
        onCancel={() => (saving ? null : setReview(null))}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

function AudioHealthDot({ status }) {
  // Tiny inline indicator next to a play/regen button. We keep it
  // intentionally subtle — just a 12px icon — so the table row doesn't
  // feel cluttered. The validation itself happens once at generation
  // time on the backend, so rendering this is essentially free.
  if (!status) return null;
  if (status === "ok") {
    return (
      <span className="inline-flex w-3.5 h-3.5 items-center justify-center text-[#10B981]" title="Audio is audible" data-testid="audio-health-ok">
        <Check className="w-3 h-3" strokeWidth={4} />
      </span>
    );
  }
  if (status === "silent") {
    return (
      <span className="inline-flex w-3.5 h-3.5 items-center justify-center text-[#F59E0B]" title="Audio is near-silent — regenerate" data-testid="audio-health-silent">
        <AlertTriangle className="w-3 h-3" strokeWidth={3} />
      </span>
    );
  }
  // missing — dim grey dot
  return (
    <span className="inline-block w-2 h-2 rounded-full bg-slate-300" title="No audio cached yet" data-testid="audio-health-missing" />
  );
}

function BulkActionsBar({ pack, onComplete }) {
  // Renders a confirm-gated dialog plus a tiny live job-progress strip.
  // Polls /admin/jobs/:id once per second while a job is running so the
  // admin can watch words tick through in real time. Concurrency-safe:
  // only one bulk job is tracked at a time per panel; clicking a second
  // bulk action while one is running just queues from the user's POV.
  const [provider, setProvider] = useState("openai");
  const [job, setJob] = useState(null); // {id, kind, total, done, succeeded, failed, silent, status}
  const [confirmKind, setConfirmKind] = useState(null); // 'audio' | 'images'

  // Poll the running job. Stops as soon as status==='done'.
  useEffect(() => {
    if (!job?.id || job.status === "done") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/jobs/${job.id}`);
        if (cancelled) return;
        setJob(data);
        if (data.status === "done") {
          if (data.failed > 0 || data.silent > 0) {
            toast.warning(`${data.kind === "audio" ? "Audio" : "Image"} regen finished: ${data.succeeded} ok, ${data.failed} failed${data.kind === "audio" ? `, ${data.silent} silent` : ""}`);
          } else {
            toast.success(`${data.kind === "audio" ? "Audio" : "Image"} regen complete: all ${data.succeeded} words OK`);
          }
          onComplete?.();
        }
      } catch (err) {
        console.warn("[bulk] poll failed", err);
      }
    };
    const t = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [job, onComplete]);

  const startAudio = async () => {
    setConfirmKind(null);
    try {
      const { data } = await api.post(`/admin/wordpacks/${pack.id}/regenerate-all-audio?provider=${provider}`);
      setJob({ ...data, kind: "audio", done: 0, succeeded: 0, failed: 0, silent: 0, status: "running" });
      toast.info(`Started bulk audio regen via ${provider} (${data.total} words)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start bulk audio regen");
    }
  };

  const startImages = async () => {
    setConfirmKind(null);
    try {
      const { data } = await api.post(`/admin/wordpacks/${pack.id}/regenerate-all-images`);
      setJob({ ...data, kind: "images", done: 0, succeeded: 0, failed: 0, silent: 0, status: "running" });
      toast.info(`Started bulk image regen (${data.total} words)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start bulk image regen");
    }
  };

  const running = job && job.status !== "done";
  const pct = job ? Math.round((job.done / Math.max(1, job.total)) * 100) : 0;

  return (
    <div className="mt-6 pt-4 border-t-2 border-dashed border-blue-100 space-y-3" data-testid="admin-pack-bulk-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-[#1E1B4B]">Regenerate all audios with:</span>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          disabled={running}
          className="rounded-xl border-2 border-blue-100 focus:border-[#4D96FF] px-3 py-1.5 outline-none font-semibold bg-white text-sm disabled:opacity-50"
          data-testid="admin-bulk-audio-provider"
        >
          <option value="openai">OpenAI</option>
          <option value="elevenlabs">ElevenLabs</option>
        </select>
        <button
          onClick={() => setConfirmKind("audio")}
          disabled={running}
          className="btn-clay-blue disabled:opacity-50"
          data-testid="admin-bulk-audio-btn"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={3} /> Regenerate
        </button>
        <span className="mx-2 h-6 w-px bg-blue-100" />
        <button
          onClick={() => setConfirmKind("images")}
          disabled={running}
          className="btn-clay-yellow disabled:opacity-50"
          data-testid="admin-bulk-images-btn"
        >
          <ImageIcon className="w-4 h-4" strokeWidth={3} /> Regenerate all images
        </button>
      </div>

      {job && (
        <div className="rounded-xl border-2 border-blue-100 bg-blue-50/50 p-3" data-testid="admin-bulk-progress">
          <div className="flex items-center justify-between text-sm font-bold text-[#1E1B4B] mb-1">
            <span>{job.kind === "audio" ? "Audio" : "Image"} regen — {job.status === "done" ? "Done" : "Working…"}</span>
            <span className="tabular-nums">{job.done}/{job.total} ({pct}%)</span>
          </div>
          <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden">
            <div className="h-full bg-[#4D96FF] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap gap-3 text-xs mt-2 text-slate-600">
            <span>✓ {job.succeeded}</span>
            <span className="text-[#F43F5E]">✗ {job.failed}</span>
            {job.kind === "audio" && <span className="text-amber-600">⚠ {job.silent} silent</span>}
            {job.errors?.slice(0, 3).map((e, i) => (
              <span key={i} className="text-[#F43F5E] truncate max-w-[260px]" title={e.error}>
                {e.word}: {e.error}
              </span>
            ))}
          </div>
        </div>
      )}

      {confirmKind && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex flex-wrap items-center gap-2" data-testid="admin-bulk-confirm">
          <span className="text-sm font-semibold text-amber-900">
            {confirmKind === "audio"
              ? `This will regenerate audio for all ${pack.word_count} words via ${provider}. Existing audio for this pack will be replaced. Continue?`
              : `This will regenerate the image for all ${pack.word_count} words. Existing images will be replaced. Continue?`}
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setConfirmKind(null)} className="btn-clay-ghost" data-testid="admin-bulk-cancel-btn">Cancel</button>
            <button
              onClick={confirmKind === "audio" ? startAudio : startImages}
              className="btn-clay"
              data-testid="admin-bulk-confirm-btn"
            >
              Yes, regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function WordCard({ word, index, busy, health = {}, onPlayVoice, onRegenAudio, onRegenAudioElevenLabs, onRegenImage, onRemoveWord }) {
  // Each card is self-contained: it owns its own image preview (always
  // visible — that's the point of card view), and three rows of actions:
  // play, regen audio per voice, regen image. Per-voice keys (`a-nova-…`,
  // `a-onyx-…`) stay isolated so spinning one button doesn't disable the
  // other.
  const aFemale = busy[`a-${WORD_VOICE_PRIMARY}-${word}`];
  const aMale = busy[`a-${WORD_VOICE_SECONDARY}-${word}`];
  const elFemale = busy[`el-${WORD_VOICE_PRIMARY}-${word}`];
  const elMale = busy[`el-${WORD_VOICE_SECONDARY}-${word}`];
  const iBusy = busy[`i-${word}`];
  return (
    <div
      className="relative rounded-2xl border-2 border-blue-100 bg-white p-3 flex flex-col items-center gap-2 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`admin-word-card-${word}`}
    >
      {typeof index === "number" && (
        <span
          className="absolute top-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-[#1D4ED8] text-[11px] font-bold tabular-nums"
          data-testid={`admin-word-card-number-${word}`}
        >
          {index}
        </span>
      )}
      <div className="self-end -mb-1">
        <button
          onClick={() => onRemoveWord(word)}
          className="text-[#F43F5E] hover:bg-rose-50 rounded-full p-1"
          title="Remove word from pack"
          data-testid={`admin-card-remove-${word}`}
        >
          <Trash2 className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>
      <WordImage word={word} visible size="sm" thumb />
      <div className="font-display text-xl font-bold text-[#1E1B4B] tracking-wide">{word}</div>

      <div className="flex flex-wrap items-center justify-center gap-1.5 w-full">
        <button
          onClick={() => onPlayVoice(word, WORD_VOICE_PRIMARY)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-[#9F1239] text-xs font-bold hover:bg-rose-100"
          title="Play female voice"
          data-testid={`admin-card-play-female-${word}`}
        >
          <Volume2 className="w-3.5 h-3.5" strokeWidth={3} /> Female
          <AudioHealthDot status={health[WORD_VOICE_PRIMARY]} />
        </button>
        <button
          onClick={() => onPlayVoice(word, WORD_VOICE_SECONDARY)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-[#1D4ED8] text-xs font-bold hover:bg-blue-100"
          title="Play male voice"
          data-testid={`admin-card-play-male-${word}`}
        >
          <Volume2 className="w-3.5 h-3.5" strokeWidth={3} /> Male
          <AudioHealthDot status={health[WORD_VOICE_SECONDARY]} />
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-1.5 w-full">
        <button
          onClick={() => onRegenAudio(word, WORD_VOICE_PRIMARY)}
          disabled={aFemale}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50/60 text-[#9F1239] text-[11px] font-bold hover:bg-rose-100 disabled:opacity-50"
          title="Regenerate female audio"
          data-testid={`admin-card-regen-female-${word}`}
        >
          <RefreshCw className={`w-3 h-3 ${aFemale ? "animate-spin" : ""}`} strokeWidth={3} /> ♀ audio
        </button>
        <button
          onClick={() => onRegenAudio(word, WORD_VOICE_SECONDARY)}
          disabled={aMale}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50/60 text-[#1D4ED8] text-[11px] font-bold hover:bg-blue-100 disabled:opacity-50"
          title="Regenerate male audio"
          data-testid={`admin-card-regen-male-${word}`}
        >
          <RefreshCw className={`w-3 h-3 ${aMale ? "animate-spin" : ""}`} strokeWidth={3} /> ♂ audio
        </button>
        <button
          onClick={() => onRegenImage(word)}
          disabled={iBusy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 text-[11px] font-bold hover:bg-amber-100 disabled:opacity-50"
          title="Regenerate image"
          data-testid={`admin-card-regen-image-${word}`}
        >
          <RefreshCw className={`w-3 h-3 ${iBusy ? "animate-spin" : ""}`} strokeWidth={3} /> Image
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-1.5 w-full">
        <button
          onClick={() => onRegenAudioElevenLabs(word, WORD_VOICE_PRIMARY)}
          disabled={elFemale}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-100 text-[#9F1239] text-[11px] font-bold hover:bg-pink-200 disabled:opacity-50"
          title="Regenerate female audio with ElevenLabs"
          data-testid={`admin-card-el-female-${word}`}
        >
          <RefreshCw className={`w-3 h-3 ${elFemale ? "animate-spin" : ""}`} strokeWidth={3} /> EL ♀
        </button>
        <button
          onClick={() => onRegenAudioElevenLabs(word, WORD_VOICE_SECONDARY)}
          disabled={elMale}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 text-[#1D4ED8] text-[11px] font-bold hover:bg-blue-200 disabled:opacity-50"
          title="Regenerate male audio with ElevenLabs"
          data-testid={`admin-card-el-male-${word}`}
        >
          <RefreshCw className={`w-3 h-3 ${elMale ? "animate-spin" : ""}`} strokeWidth={3} /> EL ♂
        </button>
      </div>
    </div>
  );
}


function PackPreviewLegend() {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-500" data-testid="pack-preview-legend">
      <span className="inline-flex items-center gap-1"><Volume2 className="w-3.5 h-3.5" strokeWidth={3} /> tap a word in Practice or Listen mode to preview audio</span>
      <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" strokeWidth={3} /> images are auto-generated and cached per word</span>
    </div>
  );
}

function ToolsPanel() {
  const [stats, setStats] = useState(null);
  const [storage, setStorage] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // last batch summary
  const [log, setLog] = useState([]);

  const loadStats = async () => {
    try {
      // Stats and storage are independent — fire in parallel so the panel
      // feels snappy even when the audio cache aggregate is the slow one.
      const [s, b] = await Promise.all([
        api.get("/admin/tools/png-stats"),
        api.get("/admin/tools/storage-breakdown"),
      ]);
      setStats(s.data);
      setStorage(b.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load stats");
    }
  };

  useEffect(() => { loadStats(); }, []);

  const onRun = async () => {
    setRunning(true);
    setLog([]);
    let totalConverted = 0;
    let totalFailed = 0;
    let totalSkipped = 0;
    let totalDeleted = 0;
    try {
      // Loop in batches of 50 so the UI stays responsive and any single
      // call doesn't blow past an ingress timeout.
      while (true) {
        const { data } = await api.post("/admin/tools/convert-png-to-webp?batch_size=50");
        setProgress(data);
        totalConverted += data.converted || 0;
        totalFailed += data.failed || 0;
        totalSkipped += data.skipped || 0;
        totalDeleted += data.png_deleted || 0;
        setLog((l) => [
          ...l,
          `Batch: found=${data.found}, converted=${data.converted}, failed=${data.failed}, remaining=${data.remaining}`,
        ]);
        if (data.done || data.found === 0) break;
      }
      toast.success(`Converted ${totalConverted} image${totalConverted === 1 ? "" : "s"} to WEBP`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion stopped");
    } finally {
      setRunning(false);
      await loadStats();
      setLog((l) => [...l, `Done. converted=${totalConverted}, failed=${totalFailed}, skipped=${totalSkipped}, png_deleted=${totalDeleted}`]);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-tools-panel">
      <div className="card-soft" data-testid="admin-tools-storage-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#4D96FF] flex items-center justify-center shadow-[0_4px_0_#1D4ED8] shrink-0">
            <Database className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Storage breakdown</h3>
            <p className="text-sm text-slate-600 font-semibold mt-0.5">
              Heaviest caches — these are exactly what ends up in the backup zip.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StorageChip
                label="Audio"
                bytes={storage?.audio?.bytes}
                count={storage?.audio?.count}
                testId="tools-storage-audio"
              />
              <StorageChip
                label="Images"
                bytes={storage?.images?.bytes}
                count={storage?.images?.count}
                tone="emerald"
                testId="tools-storage-images"
              />
              <StorageChip
                label="Definitions"
                bytes={storage?.definitions?.bytes}
                count={storage?.definitions?.count}
                tone="slate"
                testId="tools-storage-definitions"
              />
              <StorageChip
                label="Total"
                bytes={storage?.total?.bytes}
                tone="amber"
                testId="tools-storage-total"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-soft">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#22C55E] flex items-center justify-center shadow-[0_4px_0_#15803D] shrink-0">
            <ImageIcon className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Convert PNG images to WEBP</h3>
            <p className="text-sm text-slate-600 font-semibold mt-0.5">
              Walks every cached image and re-encodes it as a full-size WEBP plus a
              256x256 WEBP thumbnail. Drops the PNG bytes once both are saved. Safe to
              re-run — already-converted rows are skipped.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              <span className="chip bg-blue-50 text-blue-900 border-blue-200" data-testid="tools-total-chip">
                {stats?.total ?? "—"} total
              </span>
              <span
                className={`chip ${stats?.needs_conversion ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}
                data-testid="tools-needs-chip"
              >
                {stats?.needs_conversion ?? "—"} need conversion
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={onRun}
                disabled={running}
                className="btn-clay-green disabled:opacity-50"
                data-testid="tools-run-convert-btn"
              >
                {running ? (
                  <><Loader2 className="w-5 h-5 animate-spin" strokeWidth={3} /> Converting…</>
                ) : (
                  <><RefreshCw className="w-5 h-5" strokeWidth={3} /> Run conversion</>
                )}
              </button>
              <button
                onClick={loadStats}
                disabled={running}
                className="px-4 py-2 rounded-2xl bg-white border-4 border-blue-100 hover:border-blue-200 text-[#1E1B4B] font-bold inline-flex items-center gap-2"
                data-testid="tools-refresh-stats-btn"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={3} /> Refresh stats
              </button>
            </div>
            {progress && (
              <div className="mt-3 text-xs text-slate-700 font-semibold space-y-1">
                <div>
                  Last batch:{" "}
                  <span className="font-mono">found {progress.found}</span>
                  {" · "}<span className="font-mono">converted {progress.converted}</span>
                  {" · "}<span className="font-mono">failed {progress.failed}</span>
                  {" · "}<span className="font-mono">remaining {progress.remaining}</span>
                </div>
                {progress.failed_words?.length > 0 && (
                  <div className="text-rose-700">Failed: {progress.failed_words.slice(0, 8).join(", ")}{progress.failed_words.length > 8 ? "…" : ""}</div>
                )}
              </div>
            )}
            {log.length > 0 && (
              <details className="mt-3 text-xs text-slate-500 font-semibold" data-testid="tools-log">
                <summary className="cursor-pointer">Conversion log ({log.length} entries)</summary>
                <ul className="mt-1 space-y-0.5 max-h-48 overflow-auto">
                  {log.map((line, i) => <li key={i} className="font-mono">{line}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function StorageChip({ label, bytes, count, tone = "blue", testId }) {
  const toneCls = {
    blue: "bg-blue-50 text-blue-900 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone] || "bg-blue-50 text-blue-900 border-blue-200";
  return (
    <div className={`rounded-2xl border-2 px-3 py-2 min-w-[120px] ${toneCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="font-display text-base font-bold leading-tight">{formatBytes(bytes)}</div>
      {count != null && (
        <div className="text-[10px] font-semibold opacity-70">{count} item{count === 1 ? "" : "s"}</div>
      )}
    </div>
  );
}


function BackupPanel() {
  const [downloading, setDownloading] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [preview, setPreview] = useState(null); // { manifest, counts_in_backup, counts_current }
  const [ackBackup, setAckBackup] = useState(false);
  const [ackWipe, setAckWipe] = useState(false);
  // Last-backup metadata pulled from /admin/stats so the chip survives
  // reloads (it's not just a "since this session started" indicator).
  const [lastBackup, setLastBackup] = useState({ at: null, size: null });

  const loadLastBackup = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setLastBackup({ at: data?.last_backup_at || null, size: data?.last_backup_size || null });
    } catch { /* non-fatal — chip just stays hidden */ }
  };

  useEffect(() => { loadLastBackup(); }, []);

  // Download a full backup zip and trigger a browser save. axios is set up
  // with our auth header globally, so we just need responseType:'blob'.
  const onDownload = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/admin/backup", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      // Prefer the server-supplied filename, fall back to a local timestamp.
      let filename = "buzzspell-backup.zip";
      const disp = resp.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(disp);
      if (m) filename = m[1];
      else {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        filename = `buzzspell-backup-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.zip`;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded (${(blob.size / (1024 * 1024)).toFixed(1)} MB)`);
      // Refresh the "last backup N days ago" chip after a successful download.
      loadLastBackup();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed");
    } finally {
      setDownloading(false);
    }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please pick a .zip backup");
      return;
    }
    setRestoreFile(f);
    setPreview(null);
    setAckBackup(false);
    setAckWipe(false);
    setInspecting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/restore/inspect", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read backup");
      setRestoreFile(null);
    } finally {
      setInspecting(false);
    }
  };

  const onApplyRestore = async () => {
    if (!restoreFile || !preview) return;
    if (!ackBackup || !ackWipe) {
      toast.error("Please tick both safety checkboxes first");
      return;
    }
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("confirm", "REPLACE");
      const { data } = await api.post("/admin/restore/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.ok) {
        toast.success("Restore complete. The app will reload.");
        // Give the toast a beat to render before forcing a hard reload so
        // all in-memory caches (user, audio buffers, etc.) are dropped.
        setTimeout(() => { window.location.reload(); }, 1200);
      } else {
        toast.error(`Restore finished with ${data.errors?.length || 0} error(s). See console.`);
        console.warn("restore errors:", data.errors);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-backup-panel">
      {/* Download section */}
      <div className="card-soft">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#4D96FF] flex items-center justify-center shadow-[0_4px_0_#1D4ED8] shrink-0">
            <Download className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Download full backup</h3>
              {lastBackup.at && (() => {
                const t = new Date(lastBackup.at);
                const diffMs = Date.now() - t.getTime();
                const days = Math.floor(diffMs / 86400000);
                const hours = Math.floor(diffMs / 3600000);
                const minutes = Math.floor(diffMs / 60000);
                let label;
                if (minutes < 1) label = "just now";
                else if (hours < 1) label = `${minutes}m ago`;
                else if (days < 1) label = `${hours}h ago`;
                else if (days === 1) label = "yesterday";
                else label = `${days} days ago`;
                const stale = days >= 7;
                const sizeMb = lastBackup.size ? `${(lastBackup.size / (1024 * 1024)).toFixed(1)} MB` : null;
                return (
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 ${
                      stale
                        ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}
                    title={`Last backup ${t.toLocaleString()}${sizeMb ? ` • ${sizeMb}` : ""}`}
                    data-testid="admin-backup-last-chip"
                  >
                    <Database className="w-3.5 h-3.5" strokeWidth={3} />
                    Last backup: {label}
                  </span>
                );
              })()}
              {!lastBackup.at && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 bg-rose-50 text-rose-800 border-rose-200"
                  data-testid="admin-backup-last-chip"
                  title="No backup recorded on this server yet"
                >
                  <Database className="w-3.5 h-3.5" strokeWidth={3} />
                  No backup yet
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 font-semibold mt-0.5">
              Exports every word pack, user, school, audio file and image into a single
              <span className="font-mono"> .zip</span> you can store off-server or move to another install.
            </p>
            <ul className="text-xs text-slate-500 mt-2 list-disc ml-5 font-medium">
              <li>Includes app data only — no API keys, secrets, or environment variables.</li>
              <li>Contains user records (incl. bcrypt-hashed passwords) — store the zip securely.</li>
              <li>Filename: <span className="font-mono">buzzspell-backup-YYYY-MM-DD-HHMM.zip</span></li>
              <li>Backups can be large because audio &amp; image caches are embedded.</li>
            </ul>
            <button
              onClick={onDownload}
              disabled={downloading}
              className="btn-clay-blue mt-4"
              data-testid="admin-backup-download-btn"
            >
              {downloading ? (
                <><Loader2 className="w-5 h-5 animate-spin" strokeWidth={3} /> Building backup…</>
              ) : (
                <><Download className="w-5 h-5" strokeWidth={3} /> Download backup</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Restore section */}
      <div className="card-soft">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#FBBF24] flex items-center justify-center shadow-[0_4px_0_#B45309] shrink-0">
            <Upload className="w-6 h-6 text-[#1E1B4B]" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Restore from backup</h3>
            <p className="text-sm text-slate-600 font-semibold mt-0.5">
              Replaces ALL current data with the contents of a BuzzSpell backup zip.
              We'll show you a summary before anything changes.
            </p>
            <div className="mt-3">
              <input
                type="file"
                accept=".zip,application/zip"
                onChange={onFilePick}
                disabled={inspecting || restoring}
                className="font-semibold"
                data-testid="admin-restore-file-input"
              />
              {inspecting && (
                <div className="mt-2 inline-flex items-center gap-2 text-slate-600 font-semibold text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Reading backup…
                </div>
              )}
            </div>
          </div>
        </div>

        {preview && (
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mt-4 rounded-2xl border-4 border-amber-200 bg-amber-50 p-4 space-y-3"
            data-testid="admin-restore-preview"
          >
            <div className="flex items-center gap-2 text-amber-900">
              <ShieldAlert className="w-5 h-5" strokeWidth={3} />
              <div className="font-display font-bold text-base">Review before restoring</div>
            </div>
            <div className="text-xs text-amber-900 font-semibold">
              Backup created: {preview.manifest?.created_at || "(unknown)"}
              <span className="mx-2">·</span>
              format v{preview.manifest?.format_version}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <PreviewBlock title="Backup contains" counts={preview.counts_in_backup} testId="admin-restore-counts-backup" />
              <PreviewBlock title="Current data (will be wiped)" counts={preview.counts_current} testId="admin-restore-counts-current" tone="rose" />
            </div>
            <label className="flex items-start gap-2 text-sm font-semibold text-[#1E1B4B] cursor-pointer">
              <input
                type="checkbox"
                checked={ackBackup}
                onChange={(e) => setAckBackup(e.target.checked)}
                className="mt-1 w-4 h-4"
                data-testid="admin-restore-ack-backup"
              />
              <span>I have already downloaded a backup of the current data (or I don't need it).</span>
            </label>
            <label className="flex items-start gap-2 text-sm font-semibold text-[#9F1239] cursor-pointer">
              <input
                type="checkbox"
                checked={ackWipe}
                onChange={(e) => setAckWipe(e.target.checked)}
                className="mt-1 w-4 h-4"
                data-testid="admin-restore-ack-wipe"
              />
              <span>I understand this will REPLACE ALL CURRENT DATA. This cannot be undone.</span>
            </label>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={() => { setRestoreFile(null); setPreview(null); }}
                disabled={restoring}
                className="px-4 py-2 rounded-2xl bg-white border-4 border-slate-200 hover:border-slate-300 text-slate-700 font-bold"
                data-testid="admin-restore-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={onApplyRestore}
                disabled={restoring || !ackBackup || !ackWipe}
                className="px-5 py-2 rounded-2xl bg-[#F43F5E] text-white font-bold shadow-[0_4px_0_#9F1239] active:translate-y-[2px] active:shadow-[0_2px_0_#9F1239] disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="admin-restore-apply-btn"
              >
                {restoring ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} /> Restoring…</>
                ) : (
                  <><AlertTriangle className="w-4 h-4" strokeWidth={3} /> Replace all data &amp; restore</>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function PreviewBlock({ title, counts, testId, tone = "blue" }) {
  const toneCls = tone === "rose"
    ? "border-rose-200 bg-white"
    : "border-blue-100 bg-white";
  const labelMap = {
    schools: "Schools",
    wordlists: "Word packs",
    grade_defaults: "Grade defaults",
    users: "Users",
    progress: "User progress",
    attempts: "Test attempts",
    definitions_cache: "Definitions",
    word_audio_cache: "Audio files",
    word_images_cache: "Image files",
  };
  const entries = Object.keys(labelMap).map((k) => [labelMap[k], counts?.[k] ?? 0]);
  return (
    <div className={`rounded-xl border-2 ${toneCls} p-3`} data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{title}</div>
      <ul className="text-sm text-[#1E1B4B] space-y-0.5">
        {entries.map(([label, val]) => (
          <li key={label} className="flex justify-between">
            <span className="font-semibold">{label}</span>
            <span className="font-mono font-bold">{val}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ImageRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status_filter: filter };
      const { data } = await api.get("/admin/image-requests", { params });
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (rid, kind, msg) => {
    setBusy((b) => ({ ...b, [rid]: kind }));
    try {
      await api.post(`/admin/image-requests/${rid}/${kind}`);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${kind}`);
    } finally {
      setBusy((b) => ({ ...b, [rid]: null }));
    }
  };

  const FILTERS = ["pending", "processing", "completed", "rejected", "failed", "all"];
  const totalPending = rows.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4" data-testid="admin-image-requests-panel">
      <div className="card-soft">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#4D96FF]" strokeWidth={3} />
            <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Image generation requests</h3>
            {totalPending > 0 && (
              <span className="chip bg-amber-100 text-amber-800 border-amber-200">
                {totalPending} pending
              </span>
            )}
          </div>
          <button onClick={load} className="btn-clay-blue text-sm" data-testid="admin-image-requests-refresh">
            <RefreshCw className="w-4 h-4" strokeWidth={3} /> Refresh
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
                filter === f
                  ? "bg-[#4D96FF] text-white border-[#1D4ED8]"
                  : "bg-white text-[#1E1B4B] border-blue-200 hover:bg-blue-50"
              }`}
              data-testid={`admin-image-requests-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card-soft text-slate-500 flex items-center gap-2 font-semibold">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="card-soft text-center py-10 text-slate-500 font-semibold" data-testid="admin-image-requests-empty">
          No requests for this filter.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-image-requests-list">
          {rows.map((r) => (
            <li key={r.id} className="card-soft" data-testid={`admin-image-request-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-base font-bold text-[#1E1B4B] truncate">{r.wordlist_name}</div>
                  <div className="text-xs text-slate-600 font-semibold mt-0.5">
                    by {r.user_name || r.user_email}
                    {r.school_name && <> · {r.school_name}</>}
                    {r.grade_level && <> · {r.grade_level}</>}
                    {r.created_at && <> · {new Date(r.created_at).toLocaleString()}</>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold">
                    <span className="chip bg-blue-50 text-blue-900 border-blue-200">{r.word_count} words</span>
                    <span className="chip bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`admin-image-request-reused-${r.id}`}>{r.reused_count || 0} reused</span>
                    <span className="chip bg-amber-50 text-amber-800 border-amber-200" data-testid={`admin-image-request-new-${r.id}`}>{r.pending_generation_count || 0} new</span>
                    <StatusChip status={r.status} testId={`admin-image-request-status-${r.id}`} />
                  </div>
                  {r.error && (
                    <div className="mt-2 text-xs text-rose-700 font-semibold" data-testid={`admin-image-request-error-${r.id}`}>
                      Error: {r.error}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  {(r.status === "pending" || r.status === "failed") && (
                    <button
                      onClick={() => act(r.id, "approve", "Approved — images are generating in the background")}
                      disabled={!!busy[r.id]}
                      className="px-3 py-1.5 rounded-2xl bg-[#22C55E] text-white text-sm font-bold shadow-[0_3px_0_#15803D] active:translate-y-[2px] active:shadow-[0_1px_0_#15803D] inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`admin-image-request-approve-${r.id}`}
                    >
                      {busy[r.id] === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={3} />}
                      Approve
                    </button>
                  )}
                  {r.status === "failed" && (
                    <button
                      onClick={() => act(r.id, "retry", "Retrying — generation restarted")}
                      disabled={!!busy[r.id]}
                      className="px-3 py-1.5 rounded-2xl bg-[#FBBF24] text-[#1E1B4B] text-sm font-bold shadow-[0_3px_0_#B45309] active:translate-y-[2px] active:shadow-[0_1px_0_#B45309] inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`admin-image-request-retry-${r.id}`}
                    >
                      {busy[r.id] === "retry" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={3} />}
                      Retry
                    </button>
                  )}
                  {(r.status === "pending" || r.status === "failed") && (
                    <button
                      onClick={() => {
                        if (!window.confirm("Reject this image generation request?")) return;
                        act(r.id, "reject", "Request rejected");
                      }}
                      disabled={!!busy[r.id]}
                      className="px-3 py-1.5 rounded-2xl bg-white border-2 border-rose-300 text-[#9F1239] text-sm font-bold inline-flex items-center gap-1.5 hover:bg-rose-50 disabled:opacity-50"
                      data-testid={`admin-image-request-reject-${r.id}`}
                    >
                      {busy[r.id] === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XIcon className="w-4 h-4" strokeWidth={3} />}
                      Reject
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusChip({ status, testId }) {
  const map = {
    pending: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
    approved: { cls: "bg-blue-100 text-blue-800 border-blue-200", label: "Approved" },
    processing: { cls: "bg-blue-100 text-blue-800 border-blue-200", label: "Processing" },
    completed: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Completed" },
    rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Rejected" },
    failed: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Failed" },
  };
  const s = map[status] || { cls: "bg-slate-100 text-slate-700 border-slate-200", label: status || "?" };
  return <span className={`chip ${s.cls}`} data-testid={testId}>{s.label}</span>;
}


function DefaultsPanel() {
  const [grades, setGrades] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [packs, setPacks] = useState([]);

  const load = async () => {
    try {
      const [d, p] = await Promise.all([
        api.get("/admin/grade-defaults"),
        api.get("/admin/wordpacks"),
      ]);
      setGrades(d.data.grades);
      setDefaults(d.data.defaults);
      setPacks(p.data);
    } catch { toast.error("Could not load defaults"); }
  };
  useEffect(() => { load(); }, []);

  const currentDefault = (g) => defaults.find((d) => d.grade_level === g)?.wordlist_id || "";

  const setDefault = async (g, packId) => {
    try {
      if (!packId) await api.delete(`/admin/grade-defaults/${encodeURIComponent(g)}`);
      else await api.put("/admin/grade-defaults", { grade_level: g, wordlist_id: packId });
      load();
      toast.success("Default updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save default");
    }
  };

  const packsForGrade = (g) => packs.filter((p) => (p.available_grades || []).includes(g));

  return (
    <div className="card-soft space-y-3" data-testid="admin-defaults-panel">
      <div className="text-sm font-semibold text-slate-600">
        For each grade, pick the word pack that should be the kid's default starting pack. Only packs that have this grade in their "Available Grades" appear here.
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {grades.map((g) => {
          const opts = packsForGrade(g);
          return (
            <div key={g} className="rounded-2xl border-2 border-blue-100 p-3" data-testid={`admin-default-row-${g.replace("/", "-")}`}>
              <div className="text-sm font-bold text-[#1E1B4B] mb-1">{g}</div>
              <select
                value={currentDefault(g)}
                onChange={(e) => setDefault(g, e.target.value)}
                disabled={opts.length === 0}
                className="w-full rounded-xl border-2 border-blue-100 focus:border-[#4D96FF] px-2 py-2 outline-none font-semibold bg-white text-sm disabled:bg-slate-50"
                data-testid={`admin-default-select-${g.replace("/", "-")}`}
              >
                <option value="">— no default —</option>
                {opts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {opts.length === 0 && <div className="text-xs text-slate-400 mt-1">No packs assigned to this grade yet</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
